/* oxlint-disable typescript/no-explicit-any -- schema library requires flexible any types */
/* oxlint-disable typescript/no-unsafe-assignment, typescript/no-unsafe-call, typescript/no-unsafe-member-access -- schema library uses any types throughout */
import { BaseSchema } from '../core/base-schema'
import type * as errors from '../core/errors'
import * as types from '../core/types'
import type * as util from '../core/util'
import type { DefaultSchema } from './wrappers'
import { _default as makeDefault } from './wrappers'
import type { OptionalSchema, NullableSchema } from '../core/base-schema'

// Number schema definition
export interface NumberSchemaDef extends types.SchemaTypeDef {
  type: 'number' | 'int32' | 'uint32' | 'sint32' | 'float' | 'double'
}

export interface NumberSchemaInternals extends types.SchemaInternals<
  number,
  number
> {
  def: NumberSchemaDef
  bag: util.LoosePartial<{
    minimum: number
    maximum: number
    int: boolean
  }>
}

export interface NumberSchema extends types.Schema<
  number,
  number,
  NumberSchemaInternals
> {
  int(message?: string | { message: string | (() => string) }): this
  positive(message?: string | { message: string | (() => string) }): this
  negative(message?: string | { message: string | (() => string) }): this
  gt(
    value: number,
    message?: string | { message: string | (() => string) },
  ): this
  gte(
    value: number,
    message?: string | { message: string | (() => string) },
  ): this
  min(
    value: number,
    message?: string | { message: string | (() => string) },
  ): this
  lt(
    value: number,
    message?: string | { message: string | (() => string) },
  ): this
  lte(
    value: number,
    message?: string | { message: string | (() => string) },
  ): this
  max(
    value: number,
    message?: string | { message: string | (() => string) },
  ): this
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
  default(defaultValue: number | (() => number)): DefaultSchema<this>
  meta(metadata: Record<string, any>): this
  parse(data: unknown, params?: types.ParseContext): number
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: number }
    | { success: false; error: errors.SchemaError }
}

export const NumberSchema = types.$constructor<NumberSchema>(
  'NumberSchema',
  (inst, def: NumberSchemaDef) => {
    BaseSchema.init(inst, def)

    inst._zod.parse = (payload, _ctx) => {
      if (typeof payload.value === 'number' && Number.isFinite(payload.value))
        return payload

      payload.issues.push({
        code: 'invalid_type',
        expected: 'number',
        input: payload.value,
        inst,
      })
      return payload
    }

    const createCheck = (
      checkFn: types.CheckFn<number>,
      onattach: (schema: any) => void = () => {},
    ) => ({
      _zod: {
        def: { check: 'custom' },
        check: checkFn,
        onattach: [onattach],
      },
    })

    const addCheck = (check: types.Check<number>) => {
      return new NumberSchema({
        ...def,
        checks: [...(def.checks ?? []), check],
      })
    }

    inst.int = (message) => addCheck(createCheck((payload) => {
      if (Number.isSafeInteger(payload.value)) return
      const msg = typeof message === 'string' ? message : message?.message
      const customMessage = msg ? { message: typeof msg === 'function' ? msg() : msg } : {}
      if (!Number.isInteger(payload.value)) payload.issues.push({ code: 'invalid_type', expected: 'int', format: 'safeint', input: payload.value, inst, ...customMessage })
      else if (payload.value < Number.MIN_SAFE_INTEGER) payload.issues.push({ code: 'too_small', type: 'int', minimum: Number.MIN_SAFE_INTEGER, inclusive: true, input: payload.value, inst, continue: true, ...customMessage })
      else payload.issues.push({ code: 'too_big', type: 'int', maximum: Number.MAX_SAFE_INTEGER, inclusive: true, input: payload.value, inst, continue: true, ...customMessage })
    }, schema => { schema._zod.bag.int = true }))

    inst.positive = (message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          if (payload.value > 0) return
          const msg = getMessage()
          payload.issues.push({
            code: 'too_small',
            continue: true,
            type: 'number',
            minimum: 0,
            inclusive: false,
            input: payload.value,
            inst,
            ...(msg
              ? { message: typeof msg === 'function' ? msg() : msg }
              : {}),
          })
        }),
      )
    }

    inst.negative = (message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          if (payload.value < 0) return
          const msg = getMessage()
          payload.issues.push({
            code: 'too_big',
            continue: true,
            type: 'number',
            maximum: 0,
            inclusive: false,
            input: payload.value,
            inst,
            ...(msg
              ? { message: typeof msg === 'function' ? msg() : msg }
              : {}),
          })
        }),
      )
    }

    inst.gt = (value, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          if (payload.value > value) return
          const msg = getMessage()
          payload.issues.push({
            code: 'too_small',
            continue: true,
            type: 'number',
            minimum: value,
            inclusive: false,
            input: payload.value,
            inst,
            ...(msg
              ? { message: typeof msg === 'function' ? msg() : msg }
              : {}),
          })
        }),
      )
    }

    inst.gte = (value, message) => {
      return inst.min(value, message)
    }

    inst.min = (value, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck(
          (payload) => {
            if (payload.value >= value) return
            const msg = getMessage()
            payload.issues.push({
              code: 'too_small',
            continue: true,
              type: 'number',
              minimum: value,
              inclusive: true,
              input: payload.value,
              inst,
              ...(msg
                ? { message: typeof msg === 'function' ? msg() : msg }
                : {}),
            })
          },
          (schema) => {
            const curr = schema._zod.bag.minimum ?? Number.NEGATIVE_INFINITY
            if (value > curr) schema._zod.bag.minimum = value
          },
        ),
      )
    }

    inst.lt = (value, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          if (payload.value < value) return
          const msg = getMessage()
          payload.issues.push({
            code: 'too_big',
            continue: true,
            type: 'number',
            maximum: value,
            inclusive: false,
            input: payload.value,
            inst,
            ...(msg
              ? { message: typeof msg === 'function' ? msg() : msg }
              : {}),
          })
        }),
      )
    }

    inst.lte = (value, message) => {
      return inst.max(value, message)
    }

    inst.max = (value, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck(
          (payload) => {
            if (payload.value <= value) return
            const msg = getMessage()
            payload.issues.push({
              code: 'too_big',
            continue: true,
              type: 'number',
              maximum: value,
              inclusive: true,
              input: payload.value,
              inst,
              ...(msg
                ? { message: typeof msg === 'function' ? msg() : msg }
                : {}),
            })
          },
          (schema) => {
            const curr = schema._zod.bag.maximum ?? Number.POSITIVE_INFINITY
            if (value < curr) schema._zod.bag.maximum = value
          },
        ),
      )
    }

    inst.default = (defaultValue: number | (() => number)) => {
      return makeDefault(inst, defaultValue)
    }
  },
)

export function number(
  message?: string | { message: string | (() => string) },
): NumberSchema {
  return new NumberSchema({
    type: 'number',
    ...(message
      ? { error: typeof message === 'string' ? message : message.message }
      : {}),
  })
}

export function int32(): NumberSchema {
  return numberScalar('int32', 'varint', (schema) =>
    schema.int().min(-2_147_483_648).max(2_147_483_647),
  )
}

export function uint32(): NumberSchema {
  return numberScalar('uint32', 'varint', (schema) =>
    schema.int().min(0).max(4_294_967_295),
  )
}

export function sint32(): NumberSchema {
  return numberScalar('sint32', 'varint', (schema) =>
    schema.int().min(-2_147_483_648).max(2_147_483_647),
  )
}

export function float(): NumberSchema {
  return numberScalar('float', 'fixed32', (schema) => schema)
}

export function double(): NumberSchema {
  return numberScalar('double', 'fixed64', (schema) => schema)
}

function numberScalar(
  type: NumberSchemaDef['type'],
  wireType: types.ProtoWireType,
  apply: (schema: NumberSchema) => NumberSchema,
): NumberSchema {
  return apply(
    new NumberSchema({
      type,
      proto: {
        scalar: { type: type as types.ProtoScalarType, wireType },
      },
    }),
  )
}
